import { AnnotationShape } from "./annotation-shapes-store";

/** Pen nib pointing to the bottom left corner, shared by the polygon tool icon and cursor */
export const PEN_NIB_PATH = "M3 21l2.5-8.5L10 8l2-2 6 6-2 2-4.5 4.5z";
export const PEN_SLIT_PATH = "M3 21l4.5-4.5";
export const PEN_HOLE = { cx: 8.5, cy: 15.5, r: 1.3 };

type Hotspot = { x: number; y: number };

/** The pixel of the cursor that clicks, in the 24px box: the tips of the pin and of the pen */
const PIN_TIP: Hotspot = { x: 12, y: 22 };
const PEN_TIP: Hotspot = { x: 3, y: 21 };

/**
 * @param body SVG content, drawn black with a white outline so it reads on any background
 * @param hotspot the pixel of the cursor that clicks
 * @returns a CSS cursor value, falling back to the crosshair
 */
function svgCursor(body: string, { x, y }: Hotspot): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" stroke-linejoin="round">${body}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${x} ${y}, crosshair`;
}

const PEN = `<path d="${PEN_NIB_PATH}" fill="black" stroke="white" stroke-width="1.5"/><path d="${PEN_SLIT_PATH}" stroke="white"/><circle cx="${PEN_HOLE.cx}" cy="${PEN_HOLE.cy}" r="${PEN_HOLE.r}" fill="white"/>`;

const PIN_CURSOR = svgCursor(
  "<path d='M12 22.5s-7-7.5-7-12.5a7 7 0 0 1 14 0c0 5-7 12.5-7 12.5z' fill='black' stroke='white' stroke-width='1.5'/><circle cx='12' cy='10' r='2.5' fill='white'/>",
  PIN_TIP,
);

const PEN_CURSOR = svgCursor(PEN, PEN_TIP);

/** Like the pen tool of drawing apps, a small ring next to the pen tells the next click closes the shape */
const PEN_CLOSE_CURSOR = svgCursor(
  `${PEN}<circle cx="19" cy="19.5" r="2.5" fill="none" stroke="white" stroke-width="3"/><circle cx="19" cy="19.5" r="2.5" fill="none" stroke="black" stroke-width="1.5"/>`,
  PEN_TIP,
);

/**
 * @param shape the selected shape
 * @param canClose true when the next click closes the polygon
 * @returns the cursor telling what the next click or drag does: place a pin, drag a rectangle, add a corner, or close the polygon
 */
export function annotationShapeCursor(
  shape: AnnotationShape,
  canClose: boolean,
): string {
  switch (shape) {
    case AnnotationShape.point:
      return PIN_CURSOR;
    case AnnotationShape.rectangle:
      return "crosshair";
    case AnnotationShape.polygon:
      return canClose ? PEN_CLOSE_CURSOR : PEN_CURSOR;
  }
}
